import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getCanAccessPath } from '../../../App.util';
import { KpiType, RoutePaths } from '../../../constants';
import { LocalStorageKey } from '../../../constants/localStorageKey';
import { useThemeContext } from '../../../global-state/themeContext';
import { useLocalStorage, useProfile, useStoreInformation, useUser } from '../../../hooks';
import { Typography } from '../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../stories/atoms/containerV2/ContainerV2';
import { Icons } from '../../../stories/atoms/icon';
import { Button, IconWithText, RadioButton } from '../../../stories/molecules';
import { InputSelect } from '../../../stories/molecules/inputSelect/InputSelect';
import { homeTranslations, userPreferences } from '../../../translations';
import { sideMenuTranslations } from '../../../translations/navigation';
import { isFailureResponse } from '../../../utils';
import { getFlagUrlFromLanguage } from '../../../utils/language';
import { SelectedCategory } from '../../home/Home';
import { WidgetsCategory } from '../../home/types';
import { canViewHighlightedWidgets, categoryIcons, getPermissionToViewCategory } from '../../home/util';

type PreferencesProps = {
  isMobile: boolean;
};

const PreferencesRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <RowFlexContainer alignItems="center" justifyContent="between" margin={[4, 0]} gap={[4]}>
    <Typography>{label}</Typography>
    <ContainerV2 flex={1}>{children}</ContainerV2>
  </RowFlexContainer>
);

const getInitialCategory = (mobileKey: string | null, highlightedTargets: KpiType[]): SelectedCategory => {
  const savedIconStr = localStorage.getItem('selectedIcon');
  if (mobileKey) {
    return { selectedId: 'allWidgets', selectedName: WidgetsCategory.ALL };
  }
  if (!savedIconStr) {
    return highlightedTargets.length > 0
      ? { selectedId: 'highlightedWidgets', selectedName: WidgetsCategory.HIGHLIGHTED }
      : { selectedId: 'salesEfficiency', selectedName: WidgetsCategory.SALESEFFICIENCY };
  }
  const savedIcon = JSON.parse(savedIconStr) as SelectedCategory;
  if (savedIcon.selectedName === WidgetsCategory.HIGHLIGHTED && highlightedTargets.length === 0) {
    return { selectedId: 'salesEfficiency', selectedName: WidgetsCategory.SALESEFFICIENCY };
  }
  return savedIcon;
};

export const Preferences: FC<PreferencesProps> = ({ isMobile }) => {
  const { mode, toggleTheme } = useThemeContext();
  const { t, i18n } = useTranslation();
  const { search } = useLocation();
  const { updateProfile } = useProfile();
  const { getItem } = useLocalStorage();
  const { corporateBrandId, exclusionListCount } = useStoreInformation();
  const { widgetCategory } = homeTranslations;
  const {
    get: { profile, userPermissions, highlightedKpiTargets },
    set,
  } = useUser();

  const selectedView = getItem(LocalStorageKey.SelectedView);
  const isArea = selectedView === 'area';

  const queryParams = new URLSearchParams(search);
  const mobileDeviceKey = queryParams.get('key');

  const displayName = new Intl.DisplayNames(i18n.language, { type: 'language' });
  const defaultLanguage = profile.settings?.defaultLanguage || i18n.language;
  const initialMode = (localStorage.getItem('selectedMode') as 'light' | 'dark') || mode;
  const currentStartPage = profile.settings?.startPage;

  const [selectedLanguage, setSelectedLanguage] = useState<string>(defaultLanguage);
  const [selectedMode, setSelectedMode] = useState<'light' | 'dark'>(initialMode);

  const getPath = () => {
    if (currentStartPage) {
      return currentStartPage as RoutePaths;
    }

    return RoutePaths.HOME;
  };

  const [selectedStartPage, setSelectedStartPage] = useState<RoutePaths>(getPath());

  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>(() =>
    getInitialCategory(mobileDeviceKey, highlightedKpiTargets),
  );

  useEffect(() => {
    setSelectedMode(mode);
  }, [mode]);

  const getLanguages = (): string[] =>
    (i18n.options.supportedLngs || []).filter((lang) => lang !== 'cimode').sort();

  const renderLanguageMenuItem = (language: string) => {
    let languageDisplayName = displayName.of(language);
    if (language === 'es-MX') {
      languageDisplayName = displayName.of('es-419') ?? 'Latin American Spanish';
    }

    const flagUrl = getFlagUrlFromLanguage(language);
    return (
      <ContainerV2 display="flex" alignItems="center" gap={[1]}>
        <img src={flagUrl} alt={languageDisplayName} width={28} height={28} style={{ borderRadius: 2 }} />
        <Typography>{languageDisplayName}</Typography>
      </ContainerV2>
    );
  };

  const availableStartPages = useMemo((): { path: RoutePaths; name: string }[] => {
    const pages: { path: RoutePaths; name: string }[] = [];

    if (!userPermissions) return pages;

    const canViewSalesPage = userPermissions?.salesOverviewPage?.canRead;

    const routeTranslations = {
      [RoutePaths.HOME]: canViewSalesPage ? sideMenuTranslations.storeOverviewPage : sideMenuTranslations.home,
      [RoutePaths.WIDGETS]: sideMenuTranslations.widgets,
      [RoutePaths.TRANSACTION]: sideMenuTranslations.transaction,
      [RoutePaths.EMPLOYEES]: sideMenuTranslations.employees,
      [RoutePaths.YEARLYSTOCKTAKE]: sideMenuTranslations.yearlyStockTake,
      [RoutePaths.KPITARGET]: sideMenuTranslations.kpiTarget,
      [RoutePaths.SOLDPRODUCTS]: sideMenuTranslations.soldProducts,
      [RoutePaths.PRODUCTCATEGORY]: sideMenuTranslations.productCategory,
      [RoutePaths.PROFITANDLOSS]: sideMenuTranslations.profitAndLoss,
      [RoutePaths.WIDGETACCESSCONTROL]: sideMenuTranslations.widgetAccessControl,
      [RoutePaths.AREARESULT]: sideMenuTranslations.areaResult,
      [RoutePaths.ACCESSREQUESTS]: sideMenuTranslations.accessApproval,
      [RoutePaths.PROFILE]: sideMenuTranslations.profile,
    };

    // Filter routes based on permissions and user access
    const availableRoutes = Object.values(RoutePaths).filter((route) => {
      if (route === RoutePaths.DEVELOPMENT) return false;
      if (route === RoutePaths.SALESANALYSIS) return false;
      if (route === RoutePaths.STORESLIST) return false;
      if (route === RoutePaths.WIDGETS && !canViewSalesPage) return false;
      if (route === RoutePaths.HOME || route === RoutePaths.WIDGETS) return true;
      return true;
    });

    const allPages = availableRoutes.map((route) => {
      const translation = routeTranslations[route as keyof typeof routeTranslations];
      return {
        path: route,
        name: t(translation?.key || route, translation?.defaultValue || route.replace('/', '').replace('-', ' ')),
      };
    });

    pages.push(...allPages);

    return pages;
  }, [userPermissions, t]);

  const getStartPageMenuItems = (): string[] => {
    return availableStartPages.map((page) => page.path);
  };

  const getFilteredCategoryItems = () => {
    return categoryIcons.filter((categoryIcon) => {
      if (categoryIcon.iconName === WidgetsCategory.HIGHLIGHTED) {
        return (
          highlightedKpiTargets.length > 0 && canViewHighlightedWidgets(highlightedKpiTargets, userPermissions)
        );
      }
      return getPermissionToViewCategory(categoryIcon.iconName, userPermissions);
    });
  };

  const renderCategoryMenuItem = (category: string) => {
    const categoryIcon = categoryIcons.find((icon) => icon.iconName === category);
    if (!categoryIcon) return null;
    return (
      <ContainerV2 display="flex" alignItems="center" gap={[1]}>
        <IconWithText
          label={t(widgetCategory[categoryIcon.iconName].key, widgetCategory[categoryIcon.iconName].defaultValue)}
          iconOptions={{
            icon: categoryIcon.icon,
            size: 'small',
          }}
        />
      </ContainerV2>
    );
  };

  const renderStartPageMenuItem = (routePath: string) => {
    const pageInfo = availableStartPages.find((page) => page.path === routePath);
    return (
      <ContainerV2 display="flex" alignItems="center" gap={[1]}>
        <Typography>{pageInfo?.name || routePath}</Typography>
      </ContainerV2>
    );
  };

  const handleSettingsChange = async (settings: {
    defaultLanguage?: string;
    darkMode?: boolean;
    startPage?: string;
  }) => {
    const response = await updateProfile(profile.upn, [
      {
        op: 'add',
        path: '/settings',
        value: {
          ...profile.settings,
          ...settings,
        },
      },
    ]);

    if (isFailureResponse(response)) {
      return set({
        profile: {
          ...profile,
          settings: {
            ...settings,
          },
        },
      });
    }

    set({ profile: response.data });
    if (settings.defaultLanguage) {
      i18n.changeLanguage(settings.defaultLanguage);
    }
  };

  const handleSavePreferences = () => {
    if (i18n.language !== selectedLanguage) i18n.changeLanguage(selectedLanguage);
    if (mode !== selectedMode) toggleTheme();
    localStorage.setItem('selectedIcon', JSON.stringify(selectedCategory));
    handleSettingsChange({
      darkMode: selectedMode === 'dark',
      defaultLanguage: selectedLanguage,
      startPage: selectedStartPage,
    });
  };

  const radioButtonColor = mode === 'dark' ? 'white' : 'primary';

  const canAccessSelectedStartPage = useMemo(() => {
    const canAccessPath = getCanAccessPath(
      selectedStartPage,
      userPermissions,
      false,
      exclusionListCount,
      corporateBrandId,
    );

    return canAccessPath;
  }, [selectedStartPage, userPermissions, corporateBrandId]);

  return (
    <ColumnFlexContainer
      data-testid="preferences-section"
      backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
      hasBorder
      borderRadius={[2]}
      width="100%"
      overflow="hidden"
    >
      <ContainerV2
        backgroundColor={mode === 'dark' ? 'backgroundSelectedHover' : 'backgroundTableHeader'}
        padding={isMobile ? [3, 4] : [4, 8]}
      >
        <IconWithText
          label={t(userPreferences.title.key, userPreferences.title.defaultValue)}
          iconOptions={{ icon: Icons.SETTINGS, size: 'small' }}
          textOptions={{ type: 'h4' }}
        />
      </ContainerV2>
      <ContainerV2 padding={isMobile ? [4, 4] : [4, 8]}>
        <Typography>{t(userPreferences.subtitle.key, userPreferences.subtitle.defaultValue)}</Typography>

        <PreferencesRow label={t(userPreferences.chooseLanguage.key, userPreferences.chooseLanguage.defaultValue)}>
          <InputSelect
            menuItems={getLanguages()}
            value={selectedLanguage}
            onChange={(val) => {
              if (typeof val === 'string') {
                setSelectedLanguage(val);
              }
            }}
            isFullWidth
            renderMenuItem={renderLanguageMenuItem}
          />
        </PreferencesRow>

        <PreferencesRow label={t(userPreferences.chooseCategory.key, userPreferences.chooseCategory.defaultValue)}>
          <InputSelect
            menuItems={getFilteredCategoryItems().map((category) => category.iconName)}
            value={selectedCategory.selectedName}
            onChange={(value) => {
              if (typeof value === 'string') {
                const selected = getFilteredCategoryItems().find((category) => category.iconName === value);
                if (selected) {
                  setSelectedCategory({ selectedId: selected.iconName, selectedName: selected.iconName });
                }
              }
            }}
            isFullWidth
            renderMenuItem={renderCategoryMenuItem}
          />
        </PreferencesRow>

        {!isArea && (
          <PreferencesRow
            label={t(userPreferences.chooseStartPage.key, userPreferences.chooseStartPage.defaultValue)}
          >
            <InputSelect
              menuItems={getStartPageMenuItems()}
              value={selectedStartPage}
              onChange={(value) => {
                if (typeof value === 'string' && Object.values(RoutePaths).includes(value as RoutePaths)) {
                  setSelectedStartPage(value as RoutePaths);
                }
              }}
              helperText={
                canAccessSelectedStartPage
                  ? ''
                  : t(userPreferences.permissionText.key, userPreferences.permissionText.defaultValue)
              }
              helperTextColor={mode === 'dark' ? 'warning' : 'warningDark'}
              isFullWidth
              renderMenuItem={renderStartPageMenuItem}
            />
          </PreferencesRow>
        )}

        <RowFlexContainer alignItems="center" justifyContent="between" margin={[3, 0]}>
          <Typography>{t(userPreferences.chooseMode.key, userPreferences.chooseMode.defaultValue)}</Typography>
          <RadioButton
            labels={['Light', 'Dark']}
            selectedIndex={selectedMode === 'light' ? 0 : 1}
            onSelect={(index: number) => {
              setSelectedMode(index === 0 ? 'light' : 'dark');
            }}
            direction="horizontal"
            size="medium"
            activeColor={{
              label: radioButtonColor,
              icon: radioButtonColor,
            }}
            inactiveColor={{
              label: radioButtonColor,
              icon: radioButtonColor,
            }}
          />
        </RowFlexContainer>

        <Button
          label={t(userPreferences.button.key, userPreferences.button.defaultValue)}
          onClick={handleSavePreferences}
          fullWidth
          disabled={
            selectedLanguage === defaultLanguage &&
            selectedMode === initialMode &&
            selectedCategory.selectedName ===
              getInitialCategory(mobileDeviceKey, highlightedKpiTargets).selectedName &&
            selectedStartPage === (currentStartPage || RoutePaths.HOME)
          }
        />
      </ContainerV2>
    </ColumnFlexContainer>
  );
};
